import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { isVideoUrl } from "@/lib/contentModels";
import { galleryImageUrl } from "@/lib/imageCdn";
import { atributosDeCarga } from "@/lib/imagePriority";

type Props = {
  urls: string[];
  alt: string;
  /** Carrusel horizontal (feed) o todas las fotos en columna (detalle). */
  layout?: "carousel" | "stack";
  className?: string;
  imgClassName?: string;
  maxHeight?: number;
  /**
   * La primera galeria de la pagina: se pide ya, sin esperar al observador.
   * Depender solo de `IntersectionObserver` para lo que ya se esta viendo es
   * fragil --si no dispara, la foto no se pide nunca-- y es justo la que el
   * visitante mira mientras espera.
   */
  prioritaria?: boolean;
};

/** Fotos por delante que descargamos una vez el visitante navega el carrusel. */
const EAGER_AHEAD = 3;
/**
 * Y cuantas antes de que lo toque. Medido bajando seis pantallas del feed en
 * modo scroll: con tres por delante eran 36 fotos y 3,3 MB para cinco posts,
 * porque cada galeria que se asomaba pedia cuatro aunque el visitante pasara
 * de largo sin mirar ninguna. Quien baja leyendo ve una foto por post; la
 * siguiente se trae por si desliza, y a partir de ahi manda EAGER_AHEAD.
 */
const EAGER_INICIAL = 1;

type Dimensions = { width: number; height: number };

const galleryUrl = (url: string) => (isVideoUrl(url) ? url : galleryImageUrl(url));

export function PostImageGallery({
  urls,
  alt,
  layout = "carousel",
  className,
  imgClassName,
  maxHeight = 600,
  prioritaria = false,
}: Props) {
  const [active, setActive] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);
  /** URLs ya descargadas (y decodificadas) por el navegador. */
  const readyRef = useRef<Set<string>>(new Set());
  /** Dimensiones reales por URL: nos permiten reservar el hueco antes de pintar. */
  const dimsRef = useRef<Map<string, Dimensions>>(new Map());
  const inflightRef = useRef<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * La galeria esta en pantalla o a punto: hasta entonces no se precarga nada.
   * Arranca en true si es la prioritaria, para no quedar a merced del
   * observador en la unica foto que el visitante esta mirando ya.
   */
  const [nearViewport, setNearViewport] = useState(prioritaria);
  const [, forceRender] = useState(0);

  const sources = useMemo(() => urls.map(galleryUrl), [urls]);

  /**
   * Descarga una imagen en segundo plano y guarda sus dimensiones.
   * Los videos NO se precargan: pesan varios MB y robarian ancho de banda a
   * las fotos siguientes, que es justo lo que queremos tener listo.
   */
  const preload = useCallback((src: string | undefined, priority: "high" | "low") => {
    if (!src || isVideoUrl(src)) return;
    if (readyRef.current.has(src) || inflightRef.current.has(src)) return;
    inflightRef.current.add(src);

    const img = new Image();
    img.decoding = "async";
    // fetchPriority no esta tipado en todas las versiones de la lib DOM.
    (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = priority;
    img.onload = async () => {
      dimsRef.current.set(src, { width: img.naturalWidth, height: img.naturalHeight });
      try {
        // decode() deja el bitmap listo para pintar: el cambio de foto es instantaneo.
        await img.decode?.();
      } catch {
        // ignore
      }
      readyRef.current.add(src);
      inflightRef.current.delete(src);
      forceRender((n) => n + 1);
    };
    img.onerror = () => {
      inflightRef.current.delete(src);
    };
    img.src = src;
  }, []);

  useEffect(() => {
    if (active <= sources.length - 1) return;
    setActive(Math.max(0, sources.length - 1));
  }, [active, sources.length]);

  /**
   * Nada se precarga hasta que la galeria esta a punto de verse.
   *
   * El feed monta PAGE_SIZE galerias de golpe y la pagina mide 120.000 px de
   * alto. Sin esta condicion, la ventana de abajo pedia cuatro fotos de cada
   * una nada mas montarse: 62 peticiones y 7 MB con el scroll todavia a cero,
   * para enseñar un post. El `loading="lazy"` de las etiquetas no lo evitaba,
   * porque `new Image()` se lo salta.
   */
  useEffect(() => {
    if (layout !== "carousel" || prioritaria) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        setNearViewport(true);
        observer.disconnect();
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [layout, prioritaria]);

  // Ventana de precarga: la actual, la anterior y las siguientes EAGER_AHEAD.
  useEffect(() => {
    if (typeof window === "undefined" || layout !== "carousel" || !nearViewport) return;
    preload(sources[active], "high");
    const porDelante = active === 0 ? EAGER_INICIAL : EAGER_AHEAD;
    for (let i = 1; i <= porDelante; i += 1) preload(sources[active + i], "low");
    preload(sources[active - 1], "low");
  }, [active, sources, preload, layout, nearViewport]);

  /**
   * Una vez el usuario pasa de la primera foto, nos traemos el resto del post
   * en tiempo muerto: ya esta navegando el carrusel y lo quiere instantaneo.
   *
   * Antes esto se disparaba con solo asomarse el post al viewport, y como
   * `new Image()` se salta el `loading="lazy"` de las etiquetas, el feed se
   * descargaba 62 fotos y 7 MB de golpe sin que nadie las hubiera pedido. La
   * ventana de arriba (la actual, la anterior y EAGER_AHEAD siguientes) ya
   * cubre a quien solo pasa de largo.
   */
  useEffect(() => {
    if (typeof window === "undefined" || layout !== "carousel" || !nearViewport) return;
    if (active === 0 || sources.length <= 1) return;

    const runIdle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    const schedule = runIdle ? runIdle.bind(window) : (cb: () => void) => window.setTimeout(cb, 400);
    const handle = schedule(() => {
      sources.forEach((src) => preload(src, "low"));
    });

    return () => {
      const cancel = (window as Window & { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback;
      if (cancel) cancel(handle);
      else window.clearTimeout(handle);
    };
  }, [active, sources, preload, layout, nearViewport]);

  const activeSrc = sources[active] ?? "";
  const activeIsVideo = isVideoUrl(activeSrc);

  // El indicador solo aparece si la foto tarda de verdad; asi no parpadea
  // en el caso normal (precargada => cambio instantaneo).
  useEffect(() => {
    if (!activeSrc || activeIsVideo || readyRef.current.has(activeSrc)) {
      setShowSpinner(false);
      return;
    }
    const timer = window.setTimeout(() => setShowSpinner(true), 180);
    return () => {
      window.clearTimeout(timer);
      setShowSpinner(false);
    };
  }, [activeSrc, activeIsVideo]);

  const go = (i: number) => {
    if (i < 0 || i >= sources.length || i === active) return;
    // Cambio inmediato: el <img> conserva el fotograma anterior mientras
    // el nuevo decodifica, asi que no hay hueco en blanco ni salto de scroll.
    setActive(i);
  };

  const renderMedia = (
    url: string,
    mediaAlt: string,
    key: string,
    style?: React.CSSProperties,
    cargarYa = false,
  ) => {
    if (isVideoUrl(url)) {
      return (
        <video
          key={key}
          src={url}
          className={cn("w-full object-cover", imgClassName)}
          style={style}
          controls
          playsInline
          preload="metadata"
        />
      );
    }
    return (
      <img
        key={key}
        src={galleryUrl(url)}
        alt={mediaAlt}
        {...atributosDeCarga(cargarYa)}
        className={cn("w-full object-cover", imgClassName)}
        style={style}
      />
    );
  };

  if (!urls.length) {
    return <div className={cn("aspect-square bg-muted", className)} />;
  }

  if (layout === "stack") {
    return (
      <div className={cn(urls.length > 1 ? "space-y-3" : "", className)}>
        {urls.map((url, i) => (
          <div key={`${url}-${i}`} className="overflow-hidden rounded-xl bg-muted">
            {/* La primera del articulo es lo primero que se ve: va sin lazy. */}
            {renderMedia(
              url,
              urls.length > 1 ? `${alt} (${i + 1} de ${urls.length})` : alt,
              `${url}-${i}`,
              undefined,
              i === 0,
            )}
          </div>
        ))}
      </div>
    );
  }

  if (urls.length === 1) {
    // Con ref para que el observador tambien marque estas: en el feed hay una
    // por post y solo la que esta en pantalla debe pedirse ya.
    return (
      <div ref={containerRef} className={cn("overflow-hidden rounded-xl", className)}>
        {renderMedia(urls[0], alt, urls[0], { maxHeight }, nearViewport)}
      </div>
    );
  }

  const canGoPrev = active > 0;
  const canGoNext = active < sources.length - 1;
  const dims = dimsRef.current.get(activeSrc);

  return (
    <div ref={containerRef} className={cn("relative max-w-full", className)}>
      <div role="region" aria-label={`Galería, ${urls.length} fotos`} className="rounded-xl">
        {activeIsVideo ? (
          <video
            key={activeSrc}
            src={activeSrc}
            className={cn("block h-auto w-auto max-w-full object-contain", imgClassName)}
            style={{ maxHeight }}
            controls
            playsInline
            preload="metadata"
          />
        ) : (
          /*
           * Sin `key`: React solo cambia el `src` del mismo elemento. El
           * navegador mantiene pintada la foto anterior hasta que la nueva
           * esta lista, en vez de desmontar el <img> (que colapsaba la altura
           * y obligaba a restaurar el scroll a mano).
           */
          <img
            src={activeSrc}
            alt={`${alt} (${active + 1} de ${urls.length})`}
            width={dims?.width}
            height={dims?.height}
            {...atributosDeCarga(nearViewport)}
            className={cn("block h-auto w-auto max-w-full object-contain", imgClassName)}
            style={{ maxHeight }}
            draggable={false}
            onLoad={() => {
              readyRef.current.add(activeSrc);
              setShowSpinner(false);
            }}
          />
        )}
      </div>
      {showSpinner && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-full bg-black/45 px-2.5 py-1 text-[10px] font-medium text-white">Cargando…</div>
        </div>
      )}
      <button
        type="button"
        onClick={() => go(active - 1)}
        className={cn(
          "absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-black/45 p-1.5 text-white backdrop-blur transition",
          canGoPrev ? "opacity-100 hover:bg-black/60" : "pointer-events-none opacity-35"
        )}
        aria-label="Foto anterior"
      >
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none">
          <path d="M12 4L6 10L12 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => go(active + 1)}
        className={cn(
          "absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/40 bg-black/45 p-1.5 text-white backdrop-blur transition",
          canGoNext ? "opacity-100 hover:bg-black/60" : "pointer-events-none opacity-35"
        )}
        aria-label="Foto siguiente"
      >
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none">
          <path d="M8 4L14 10L8 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className="absolute right-2 top-2 rounded-md bg-black/55 px-2 py-0.5 text-[10px] font-semibold text-white">
        {active + 1}/{urls.length}
      </div>
      <div className="pointer-events-none absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
        {urls.map((_, i) => (
          <button
            key={i}
            type="button"
            className={cn(
              "pointer-events-auto h-1.5 w-1.5 rounded-full transition-colors",
              i === active ? "bg-white shadow-sm" : "bg-white/45 hover:bg-white/70"
            )}
            aria-label={`Ir a la foto ${i + 1} de ${urls.length}`}
            aria-pressed={i === active}
            onClick={() => go(i)}
          />
        ))}
      </div>
    </div>
  );
}
